import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  Grid,
  Divider,
  Chip,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  alpha,
} from "@mui/material";
import {
  Eye,
  FileSpreadsheet,
  Search,
  ScanLine,
  X,
  Camera,
  Zap,
  ZapOff,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";
import * as XLSX from "xlsx";

import BackBtn from "../../../widgets/BackBtn";
import axios from "axios";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { API_ENDPOINT } from "../../../constants/constants";
import { getHeader } from "../../../utility/utility";

// Reuse the web app's token helper. getHeader() returns Axios config { headers }.
const apiUrl = (path) =>
  `${API_ENDPOINT.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
const requestConfig = () => {
  const config = getHeader();
  return {
    ...config,
    headers: { ...config.headers, "Content-Type": "application/json" },
  };
};
const errorText = (error, fallback) => {
  const message = error?.response?.data?.message;
  return typeof message === "string" ? message : error?.message || fallback;
};

// Serialize camera lifecycles, including StrictMode cleanup and rapid reopening.
let cameraQueue = Promise.resolve();
let scannerSequence = 0;
const queueCamera = (operation) => {
  const result = cameraQueue.then(operation);
  cameraQueue = result.catch(() => {});
  return result;
};

function CameraScanner({ onScan }) {
  const hostRef = useRef(null);
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [cameraId, setCameraId] = useState("");
  const [cameras, setCameras] = useState([]);
  const [attempt, setAttempt] = useState(0);
  const [starting, setStarting] = useState(true);
  const [cameraError, setCameraError] = useState("");
  const [controlError, setControlError] = useState("");
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchBusy, setTorchBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let accepted = false;
    let scanner;
    let reader;
    let started = false;
    const host = hostRef.current;
    setStarting(true);
    setCameraError("");
    setControlError("");
    setHasTorch(false);
    setTorchOn(false);

    queueCamera(async () => {
      if (cancelled) return;
      try {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            "Camera access requires HTTPS or localhost and a browser with camera support."
          );
        }
        reader = document.createElement("div");
        reader.id = `lead-qr-reader-${++scannerSequence}`;
        host.appendChild(reader);
        scanner = new Html5Qrcode(reader.id, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        scannerRef.current = scanner;
        await scanner.start(
          cameraId || { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (width, height) => {
              const side = Math.floor(Math.min(width, height) * 0.72);
              return { width: side, height: side };
            },
          },
          (decodedText) => {
            if (cancelled || accepted || !decodedText) return;
            accepted = true;
            // Preserve the exact decoded value, just like the Flutter app.
            onScanRef.current(decodedText);
          },
          () => {} // A frame without a QR code is expected, not a UI error.
        );
        started = true;
        if (cancelled) return;
        setStarting(false);
        try {
          setHasTorch(!!scanner.getRunningTrackCapabilities()?.torch);
        } catch {
          /* Some browsers do not expose torch capabilities. */
        }
        // Enumerate after permission, without opening another camera stream.
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled)
            setCameras(
              devices.filter((d) => d.kind === "videoinput" && d.deviceId)
            );
        } catch {
          /* Scanning can still work without camera enumeration. */
        }
      } catch (error) {
        if (cancelled) return;
        const text = String(error?.message || error);
        const name = error?.name || text;
        setCameraError(
          /NotAllowed|Permission|denied/i.test(name)
            ? "Camera permission was denied. Allow camera access in your browser's site settings, then retry."
            : /NotFound|DevicesNotFound/i.test(name)
            ? "No camera was found. Connect a camera or open this page on your phone."
            : /NotReadable|TrackStart/i.test(name)
            ? "The camera is unavailable. Close other apps using it, then retry."
            : text || "Unable to start the camera. Please retry."
        );
        setStarting(false);
      }
    });

    return () => {
      cancelled = true;
      queueCamera(async () => {
        try {
          if (started) await scanner.stop();
        } catch {
          // Also release the actual media tracks if the library stop fails.
        } finally {
          const video = reader?.querySelector("video");
          video?.srcObject?.getTracks?.().forEach((track) => track.stop());
          try {
            scanner?.clear();
          } catch {
            /* Already removed. */
          }
          reader?.remove();
          if (scannerRef.current === scanner) scannerRef.current = null;
        }
      });
    };
  }, [cameraId, attempt]);

  const toggleTorch = async () => {
    const scanner = scannerRef.current;
    if (!scanner || torchBusy) return;
    setTorchBusy(true);
    setControlError("");
    try {
      await scanner.applyVideoConstraints({ advanced: [{ torch: !torchOn }] });
      if (scannerRef.current === scanner) setTorchOn(!torchOn);
    } catch {
      setControlError("The flashlight could not be changed on this camera.");
    } finally {
      setTorchBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          position: "relative",
          bgcolor: "#0f172a",
          borderRadius: 3,
          overflow: "hidden",
          minHeight: 260,
        }}
      >
        <Box
          ref={hostRef}
          sx={{ width: "100%", "& video": { display: "block" } }}
        />
        {starting && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "#0f172a",
              color: "white",
            }}
          >
            <CircularProgress color="inherit" size={32} />
            <Typography>Opening camera…</Typography>
          </Stack>
        )}
        {cameraError && (
          <Stack alignItems="center" spacing={2} sx={{ p: 3, color: "white" }}>
            <Camera size={36} />
            <Typography align="center">Camera unavailable</Typography>
          </Stack>
        )}
      </Box>
      {cameraError ? (
        <>
          <Alert severity="error">{cameraError}</Alert>
          <Button
            startIcon={<RotateCcw size={18} />}
            onClick={() => setAttempt((n) => n + 1)}
          >
            Retry camera
          </Button>
        </>
      ) : (
        <Typography align="center" color="text.secondary" variant="body2">
          Align the badge QR code within the frame. It will scan automatically.
        </Typography>
      )}
      {controlError && <Alert severity="warning">{controlError}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center">
        {cameras.length > 1 && (
          <TextField
            select
            fullWidth
            size="small"
            label="Camera"
            value={cameraId}
            disabled={starting || torchBusy}
            onChange={(e) => {
              setStarting(true);
              setCameraId(e.target.value);
            }}
          >
            <MenuItem value="">Automatic (prefer rear camera)</MenuItem>
            {cameras.map((camera, index) => (
              <MenuItem key={camera.deviceId} value={camera.deviceId}>
                {camera.label || `Camera ${index + 1}`}
              </MenuItem>
            ))}
          </TextField>
        )}
        {hasTorch && !cameraError && (
          <Button
            variant={torchOn ? "contained" : "outlined"}
            disabled={starting || torchBusy}
            onClick={toggleTorch}
            startIcon={torchOn ? <ZapOff size={18} /> : <Zap size={18} />}
            sx={{ flexShrink: 0, textTransform: "none" }}
          >
            {torchOn ? "Light off" : "Light on"}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function LeadScannerDialog({ onClose, onSaved }) {
  const fullScreen = useMediaQuery("(max-width:600px)");
  const [ready, setReady] = useState(false);
  const [code, setCode] = useState("");
  const [comment, setComment] = useState("");
  const [leadType, setLeadType] = useState("Hot");
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");
  const savingRef = useRef(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const close = () => {
    if (!savingRef.current) onClose();
  };
  const continueScanning = () => {
    setCode("");
    setComment("");
    setLeadType("Hot");
    setSubmitError("");
    setSuccess("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!code || savingRef.current || success) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError("");
    let savedMessage;
    try {
      const response = await axios.post(
        apiUrl("user/exhibitor-user-scan"),
        { barcode_id: code, comment, leadType },
        { ...requestConfig(), timeout: 30000 }
      );
      // Match Flutter's success contract: HTTP 200.
      if (response.status !== 200 || response.data?.success === false) {
        throw new Error(
          response.data?.message || "Failed to scan. Invalid code."
        );
      }
      savedMessage =
        typeof response.data?.message === "string"
          ? response.data.message || "Lead saved successfully!"
          : "Lead saved successfully!";
    } catch (error) {
      if (mountedRef.current) {
        setSubmitError(
          error?.response
            ? errorText(error, "Failed to save the lead.")
            : "Could not confirm whether the lead was saved. Check your connection and the leads list before retrying."
        );
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
    if (savedMessage && mountedRef.current) {
      setSuccess(savedMessage);
      // Refresh separately: a GET failure must not turn a successful POST into a failed save.
      onSaved();
    }
  };

  return (
    <Dialog
      open
      onClose={close}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      disableEscapeKeyDown={saving}
      aria-labelledby="scan-leads-title"
      TransitionProps={{ onEntered: () => setReady(true) }}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <DialogTitle
        id="scan-leads-title"
        sx={{ bgcolor: "#0f172a", color: "white", py: 2 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ScanLine size={26} />
            <Box>
              <Typography
                component="span"
                sx={{ display: "block", fontWeight: 800, fontSize: 20 }}
              >
                Scan leads
              </Typography>
              <Typography
                component="span"
                sx={{ display: "block", color: "#cbd5e1", fontSize: 12 }}
              >
                Scan a badge. Capture a connection.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Close scanner"
            onClick={close}
            disabled={saving}
            sx={{ color: "inherit" }}
          >
            <X size={22} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: "24px !important" }}>
        {!code && ready && <CameraScanner onScan={setCode} />}
        {code && !success && (
          <Box component="form" id="lead-scan-form" onSubmit={submit}>
            <Stack spacing={3}>
              <Alert severity="success" icon={<ScanLine size={22} />}>
                QR code captured. Add the lead details below.
              </Alert>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#eef2ff",
                  border: "1px solid #c7d2fe",
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  SCANNED ID
                </Typography>
                <Typography
                  fontWeight={700}
                  sx={{
                    overflowWrap: "anywhere",
                    maxHeight: 120,
                    overflow: "auto",
                  }}
                >
                  {code}
                </Typography>
              </Box>
              <TextField
                label="Add remark"
                placeholder="What did you discuss? Any follow-up needed?"
                multiline
                minRows={3}
                fullWidth
                value={comment}
                disabled={saving}
                onChange={(e) => setComment(e.target.value)}
              />
              <Box>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Lead type
                </Typography>
                <Stack direction="row" spacing={1}>
                  {[
                    { name: "Hot", color: "#dc2626" },
                    { name: "Warm", color: "#d97706" },
                    { name: "Cold", color: "#2563eb" },
                  ].map((item) => (
                    <Button
                      key={item.name}
                      type="button"
                      fullWidth
                      disabled={saving}
                      aria-pressed={leadType === item.name}
                      onClick={() => setLeadType(item.name)}
                      sx={{
                        py: 1.3,
                        fontWeight: 800,
                        textTransform: "none",
                        border: "2px solid",
                        borderRadius: 2,
                        borderColor:
                          leadType === item.name ? item.color : "#e2e8f0",
                        bgcolor:
                          leadType === item.name
                            ? alpha(item.color, 0.1)
                            : "transparent",
                        color: item.color,
                      }}
                    >
                      {item.name}
                    </Button>
                  ))}
                </Stack>
              </Box>
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
          </Box>
        )}
        {success && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <Avatar
              sx={{
                width: 76,
                height: 76,
                bgcolor: "#dcfce7",
                color: "#15803d",
              }}
            >
              <CheckCircle2 size={42} />
            </Avatar>
            <Typography variant="h5" fontWeight={800}>
              Lead saved
            </Typography>
            <Typography align="center" color="text.secondary">
              {success}
            </Typography>
            <Chip label={`${leadType} lead`} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          borderTop: "1px solid #e2e8f0",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          onClick={close}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {success ? "Go back" : "Close"}
        </Button>
        {code && !success && (
          <>
            <Button
              disabled={saving}
              onClick={continueScanning}
              sx={{ textTransform: "none" }}
            >
              Skip / scan another
            </Button>
            <Button
              form="lead-scan-form"
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CheckCircle2 size={18} />
                )
              }
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {saving ? "Submitting…" : "Save lead"}
            </Button>
          </>
        )}
        {success && (
          <Button
            variant="contained"
            startIcon={<ScanLine size={18} />}
            onClick={continueScanning}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            Continue scanning
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const Leads = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isError = !!error;
  const [scannerOpen, setScannerOpen] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refreshLeads = useCallback(() => setRefreshVersion((n) => n + 1), []);

  // Fetch directly so successful scans can refresh without assuming the custom hook's API.
  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    const load = async () => {
      try {
        const response = await axios.get(apiUrl("user/get-scanned-users"), {
          ...requestConfig(),
          signal: controller.signal,
          timeout: 30000,
        });
        if (!Array.isArray(response.data))
          throw new Error("Unexpected leads response: expected an array.");
        if (!controller.signal.aborted) setData(response.data);
      } catch (error) {
        if (!controller.signal.aborted)
          setError(new Error(errorText(error, "Failed to fetch leads")));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [refreshVersion]);

  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);

  const handleView = (lead) => {
    setSelectedLead(lead);
  };

  const handleClose = () => {
    setSelectedLead(null);
  };

  // 📊 Lead type counts
  const leadTypeStats = useMemo(() => {
    if (!data) return { total: 0, Hot: 0, Warm: 0, Cold: 0 };
    return data.reduce(
      (acc, lead) => {
        acc.total += 1;
        const t = lead.leadType || "Cold";
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      },
      { total: 0, Hot: 0, Warm: 0, Cold: 0 }
    );
  }, [data]);

  // 🔍 Search + Type Filter
  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((lead) => {
      const s = (search || "").toLowerCase();

      const matchesSearch =
        (lead.firstName || "").toLowerCase().includes(s) ||
        (lead.lastName || "").toLowerCase().includes(s) ||
        (lead.email || "").toLowerCase().includes(s) ||
        (lead.companyName || "").toLowerCase().includes(s) ||
        (lead.leadType || "").toLowerCase().includes(s);

      const matchesType = activeTypeFilter
        ? (lead.leadType || "Cold") === activeTypeFilter
        : true;

      return matchesSearch && matchesType;
    });
  }, [search, data, activeTypeFilter]);
  // 📤 EXPORT XLSX
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "Leads.xlsx");
  };

  return (
    <Box>
      <BackBtn />
      {scannerOpen && (
        <LeadScannerDialog
          onClose={() => setScannerOpen(false)}
          onSaved={refreshLeads}
        />
      )}
      <Card
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 4,
          border: "1px solid #eef2f6",
          boxShadow: "0 4px 24px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #f1f5f9",
            backgroundColor: "#f8fafc",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: "#6366f1", width: 40, height: 40 }}>
                <FileSpreadsheet size={20} color="white" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#1e293b">
                  Leads Captured
                </Typography>
                <Typography variant="body2" color="#64748b" fontWeight={500}>
                  Manage and export your scanned leads
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<ScanLine size={23} />}
                onClick={() => setScannerOpen(true)}
                sx={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  color: "white",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 800,
                  boxShadow: "0 6px 18px rgba(79,70,229,0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #4338ca, #6d28d9)",
                    boxShadow: "0 8px 22px rgba(79,70,229,0.4)",
                  },
                }}
              >
                Scan leads
              </Button>
              <Button
                variant="outlined"
                disabled={data.length === 0}
                startIcon={<FileSpreadsheet size={18} />}
                onClick={exportToExcel}
                sx={{
                  borderColor: "#cbd5e1",
                  color: "#334155",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#eef2ff", borderColor: "#a5b4fc" },
                }}
              >
                Export All
              </Button>
            </Stack>
          </Stack>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* Lead Type Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                label: "Total Leads",
                count: leadTypeStats.total,
                color: "#6366f1",
                emoji: "📋",
                key: null,
              },
              {
                label: "Hot Leads",
                count: leadTypeStats.Hot,
                color: "#ef4444",
                emoji: "🔥",
                key: "Hot",
              },
              {
                label: "Warm Leads",
                count: leadTypeStats.Warm,
                color: "#f59e0b",
                emoji: "🌤️",
                key: "Warm",
              },
              {
                label: "Cold Leads",
                count: leadTypeStats.Cold,
                color: "#3b82f6",
                emoji: "❄️",
                key: "Cold",
              },
            ].map((stat) => {
              const isActive = activeTypeFilter === stat.key;
              return (
                <Grid item xs={6} sm={3} key={stat.label}>
                  <Card
                    elevation={0}
                    onClick={() =>
                      setActiveTypeFilter(isActive ? null : stat.key)
                    }
                    sx={{
                      borderRadius: 3,
                      border: "1.5px solid",
                      borderColor: isActive
                        ? alpha(stat.color, 0.55)
                        : "#eef2f6",
                      background: isActive
                        ? `linear-gradient(135deg, ${alpha(
                            stat.color,
                            0.1
                          )} 0%, ${alpha(stat.color, 0.04)} 100%)`
                        : "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
                      cursor: "pointer",
                      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: `0 8px 24px -6px ${alpha(stat.color, 0.25)}`,
                        borderColor: alpha(stat.color, 0.4),
                      },
                    }}
                  >
                    <Box sx={{ p: 2.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontWeight: 700,
                              letterSpacing: 0.7,
                              textTransform: "uppercase",
                              fontSize: "0.65rem",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                              color: isActive ? stat.color : "#0f172a",
                              lineHeight: 1.1,
                              letterSpacing: "-0.5px",
                            }}
                          >
                            {isLoading ? "…" : stat.count}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            fontSize: "1.6rem",
                            lineHeight: 1,
                            mt: 0.25,
                            filter: isActive ? "none" : "grayscale(30%)",
                            transition: "filter 0.2s",
                          }}
                        >
                          {stat.emoji}
                        </Box>
                      </Stack>
                      {isActive && (
                        <Box
                          sx={{
                            mt: 1.5,
                            height: 3,
                            borderRadius: 2,
                            background: `linear-gradient(90deg, ${
                              stat.color
                            }, ${alpha(stat.color, 0.35)})`,
                          }}
                        />
                      )}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Search Bar */}
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Search leads by name, email, company, or type..."
              variant="outlined"
              size="medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search
                    size={20}
                    color="#64748b"
                    style={{ marginRight: 12 }}
                  />
                ),
                sx: {
                  borderRadius: 3,
                  bgcolor: "#f8fafc",
                  "& fieldset": { borderColor: "#e2e8f0" },
                },
              }}
            />
          </Box>

          {/* Loading */}
          {isLoading && (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {isError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error?.message || "Failed to fetch leads"}
              <Button size="small" onClick={refreshLeads} sx={{ ml: 1 }}>
                Retry loading
              </Button>
            </Alert>
          )}

          {/* TABLE */}
          <TableContainer sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                    Lead Info
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                    Company
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                    Date
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, color: "#475569" }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData?.map((lead) => (
                  <TableRow
                    key={lead._id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            bgcolor: "#e0e7ff",
                            color: "#4f46e5",
                            fontWeight: 700,
                          }}
                        >
                          {lead.firstName?.charAt(0)}
                          {lead.lastName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            color="#1e293b"
                          >
                            {lead.firstName} {lead.lastName}
                          </Typography>
                          <Typography variant="body2" color="#64748b">
                            {lead.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#334155"
                      >
                        {lead.companyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={lead.leadType || "Unknown"}
                        sx={{
                          fontWeight: 700,
                          borderRadius: 1.5,
                          color:
                            lead.leadType === "Hot"
                              ? "#b91c1c"
                              : lead.leadType === "Warm"
                              ? "#b45309"
                              : "#1d4ed8",
                          bgcolor:
                            lead.leadType === "Hot"
                              ? "#fef2f2"
                              : lead.leadType === "Warm"
                              ? "#fffbeb"
                              : "#eff6ff",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {lead.createdOn ? (
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#334155"
                          >
                            {new Date(lead.createdOn).toLocaleDateString(
                              undefined,
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </Typography>
                          <Typography variant="caption" color="#94a3b8">
                            {new Date(lead.createdOn).toLocaleTimeString(
                              undefined,
                              { hour: "2-digit", minute: "2-digit" }
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="#94a3b8">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleView(lead)}
                        sx={{
                          color: "#6366f1",
                          bgcolor: "#eef2ff",
                          "&:hover": { bgcolor: "#e0e7ff" },
                        }}
                      >
                        <Eye size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredData.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No leads found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* VIEW DETAILS DIALOG */}
          <Dialog
            open={!!selectedLead}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 4, p: 1 },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 800,
                fontSize: "1.25rem",
                color: "#1e293b",
                pb: 1,
              }}
            >
              Lead Information
            </DialogTitle>
            <Divider sx={{ mx: 3 }} />

            <DialogContent sx={{ mt: 1 }}>
              {selectedLead && (
                <Stack spacing={3}>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: "#e0e7ff",
                        color: "#4f46e5",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                      }}
                    >
                      {selectedLead.firstName?.charAt(0)}
                      {selectedLead.lastName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1e293b">
                        {selectedLead.firstName} {selectedLead.lastName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="#64748b"
                        fontWeight={500}
                      >
                        {selectedLead.email}
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        p={2}
                        borderRadius={3}
                        bgcolor="#f8fafc"
                        border="1px solid #e2e8f0"
                      >
                        <Typography
                          variant="caption"
                          color="#94a3b8"
                          fontWeight={700}
                          textTransform="uppercase"
                        >
                          Company
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color="#334155"
                          mt={0.5}
                        >
                          {selectedLead.companyName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        p={2}
                        borderRadius={3}
                        bgcolor="#f8fafc"
                        border="1px solid #e2e8f0"
                      >
                        <Typography
                          variant="caption"
                          color="#94a3b8"
                          fontWeight={700}
                          textTransform="uppercase"
                        >
                          Lead Type
                        </Typography>
                        <Box mt={0.5}>
                          <Chip
                            label={selectedLead.leadType || "Unknown"}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              borderRadius: 1.5,
                              color:
                                selectedLead.leadType === "Hot"
                                  ? "#b91c1c"
                                  : selectedLead.leadType === "Warm"
                                  ? "#b45309"
                                  : "#1d4ed8",
                              bgcolor:
                                selectedLead.leadType === "Hot"
                                  ? "#fef2f2"
                                  : selectedLead.leadType === "Warm"
                                  ? "#fffbeb"
                                  : "#eff6ff",
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Comment */}
                  {selectedLead.comment && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="#94a3b8"
                        fontWeight={700}
                        textTransform="uppercase"
                        mb={1}
                        display="block"
                      >
                        Notes
                      </Typography>
                      <Box
                        p={2.5}
                        borderRadius={3}
                        bgcolor="#fdf8f6"
                        border="1px solid #fde0d9"
                      >
                        <Typography
                          variant="body2"
                          color="#475569"
                          sx={{ fontStyle: "italic" }}
                        >
                          "{selectedLead.comment}"
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Extra */}
                  <Box p={2} borderRadius={3} bgcolor="#f1f5f9">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="caption"
                          color="#94a3b8"
                          display="block"
                        >
                          Barcode ID
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#475569"
                        >
                          {selectedLead.barcode_id}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="caption"
                          color="#94a3b8"
                          display="block"
                        >
                          Username
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#475569"
                        >
                          {selectedLead.username}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="#94a3b8"
                          display="block"
                        >
                          Scanned On
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#475569"
                        >
                          {new Date(selectedLead.createdOn).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              )}

              <Stack direction="row" justifyContent="flex-end" mt={4}>
                <Button
                  variant="contained"
                  onClick={handleClose}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    bgcolor: "#0f172a",
                    "&:hover": { bgcolor: "#334155" },
                  }}
                >
                  Done
                </Button>
              </Stack>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Leads;
